import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Divider,
  Flex,
  Spacer,
  InputGroup,
  InputLeftElement,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
} from "@chakra-ui/react";
import {
  FiEye,
  FiMail,
  FiSearch,
  FiFilter,
  FiDownload,
  FiUser,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiXCircle,
  FiFileText,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import preBoardingService from "../services/preBoardingService";
import { usePreBoarding } from "../../../context/PreBoardingContext";
import preBoardingDocumentService from "../../../services/preBoardingDocumentService";
import DocumentUpload from "./DocumentUpload";

const PreBoardingDashboard = () => {
  // Legacy packet state
  const [packets, setPackets] = useState([]);
  const [stats, setStats] = useState({});
  const [localLoading, setLocalLoading] = useState(true);

  // New document-based state from context
  const preBoardingContext = usePreBoarding();

  const [filters, setFilters] = useState({
    status: "",
    search: "",
    overdue: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
  });
  const [selectedPacket, setSelectedPacket] = useState(null);
  const [reminderPacket, setReminderPacket] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [documentStats, setDocumentStats] = useState({});
  const [expiringDocuments, setExpiringDocuments] = useState([]);

  const navigate = useNavigate();
  const toast = useToast();
  const {
    isOpen: isReminderOpen,
    onOpen: onReminderOpen,
    onClose: onReminderClose,
  } = useDisclosure();
  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
  } = useDisclosure();
  const {
    isOpen: isDocumentStatsOpen,
    onOpen: onDocumentStatsOpen,
    onClose: onDocumentStatsClose,
  } = useDisclosure();
  const {
    isOpen: isUploadOpen,
    onOpen: onUploadOpen,
    onClose: onUploadClose,
  } = useDisclosure();
  const {
    isOpen: isCreatePacketOpen,
    onOpen: onCreatePacketOpen,
    onClose: onCreatePacketClose,
  } = useDisclosure();
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [acceptedOffers, setAcceptedOffers] = useState([]);
  const [acceptedOffersLoading, setAcceptedOffersLoading] = useState(false);

  // Fetch packets when filters (explicit fields) or pagination change
  useEffect(() => {
    fetchPackets();
    // Only fetch packets when filters or page/limit change
  }, [
    filters.status,
    filters.search,
    filters.overdue,
    pagination.page,
    pagination.limit,
  ]);

  // Fetch global stats / expiring documents / verification stats once on mount
  useEffect(() => {
    fetchStats();
    fetchExpiringDocuments();
    fetchVerificationStats();
  }, []);

  // Debounce search input to avoid spamming requests while typing
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }));
      // reset to page 1 whenever search changes
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 500);

    return () => clearTimeout(t);
  }, [searchTerm]);

  // When create-packet modal opens, fetch recent offers
  useEffect(() => {
    if (isCreatePacketOpen) {
      fetchRecentOffers();
    } else {
      setSelectedOfferId("");
    }
  }, [isCreatePacketOpen]);

  const fetchPackets = async () => {
    try {
      setLocalLoading(true);
      const response = await preBoardingService.getAllPackets({
        ...filters,
        ...pagination,
      });
      setPackets(response.data.packets || []);

      // Normalize pagination coming from the API to our local shape.
      // API returns { currentPage, totalPages, totalPackets, hasNext, hasPrev }
      const apiPag = response.data.pagination || {};
      setPagination((prev) => {
        const normalized = {
          page: apiPag.currentPage || prev.page,
          limit: prev.limit,
          totalPages: apiPag.totalPages ?? prev.totalPages,
          totalPackets: apiPag.totalPackets ?? prev.totalPackets,
          hasNext: apiPag.hasNext ?? prev.hasNext,
          hasPrev: apiPag.hasPrev ?? prev.hasPrev,
        };

        // Only update state if something actually changed to avoid
        // triggering unnecessary re-renders/useEffect runs.
        const changed =
          normalized.page !== prev.page ||
          normalized.limit !== prev.limit ||
          normalized.totalPages !== prev.totalPages ||
          normalized.totalPackets !== prev.totalPackets ||
          normalized.hasNext !== prev.hasNext ||
          normalized.hasPrev !== prev.hasPrev;

        return changed ? normalized : prev;
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch pre-boarding packets",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLocalLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await preBoardingService.getStats();
      setStats(response.data.stats || {});
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchExpiringDocuments = async () => {
    try {
      const response = await preBoardingDocumentService.getExpiringDocuments(
        30
      );
      setExpiringDocuments(response.data.documents || []);
    } catch (error) {
      console.error("Failed to fetch expiring documents:", error);
    }
  };

  const fetchVerificationStats = async () => {
    try {
      const response = await preBoardingDocumentService.getVerificationStats();
      setDocumentStats(response.data.stats || {});
    } catch (error) {
      console.error("Failed to fetch verification stats:", error);
    }
  };

  const handleViewDocumentDetails = async (candidateId) => {
    try {
      setSelectedCandidate(candidateId);
      await preBoardingContext.getCandidateDocuments(candidateId);
      await preBoardingContext.getSubmissionSummary(candidateId);
      onDocumentStatsOpen();
    } catch (error) {
      console.error("Failed to fetch candidate document details:", error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleSendReminder = async (packet) => {
    try {
      await preBoardingService.sendReminder(packet._id);
      toast({
        title: "Success",
        description: "Reminder sent successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setReminderPacket(null);
      onReminderClose();
      fetchPackets(); // Refresh to update reminder count
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send reminder",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchRecentOffers = async () => {
    try {
      setOffersLoading(true);
      // Use axios with proper auth headers
      const response = await fetch("/api/v1/offers?limit=20", {
        headers: {
          Authorization: localStorage.getItem("token")
            ? `Bearer ${localStorage.getItem("token")}`
            : "",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch offers");
      const json = await response.json();
      // API returns { success, data: [...] }
      const list = json.data || [];
      setOffers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching offers:", err);
      setOffers([]);
    } finally {
      setOffersLoading(false);
    }
  };

  const fetchAcceptedOffers = async () => {
    try {
      setAcceptedOffersLoading(true);
      const response = await fetch("/api/v1/offers?status=accepted&limit=50", {
        headers: {
          Authorization: localStorage.getItem("token")
            ? `Bearer ${localStorage.getItem("token")}`
            : "",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch accepted offers");
      const json = await response.json();
      const list = json.data || [];
      setAcceptedOffers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching accepted offers:", err);
      setAcceptedOffers([]);
    } finally {
      setAcceptedOffersLoading(false);
    }
  };

  const handleCreatePacketFromOffer = async () => {
    if (!selectedOfferId) {
      toast({ title: "Select an offer", status: "warning", duration: 3000 });
      return;
    }

    try {
      const res = await preBoardingService.createPacket(selectedOfferId);
      // preBoardingService returns response.data — try to find created packet id
      const packet = res.data?.packet || res.packet || res.data || res;
      toast({ title: "Packet created", status: "success", duration: 3000 });
      onCreatePacketClose();
      fetchPackets();
      // navigate to packet review page if we have an id
      const packetId = packet?._id || packet?.id || packet;
      if (packetId) {
        navigate(`/recruiting/preboarding/${packetId}`);
      }
    } catch (error) {
      console.error("Error creating packet:", error);
      toast({
        title: "Failed to create packet",
        status: "error",
        duration: 4000,
      });
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      initiated: "blue",
      in_progress: "yellow",
      pending_review: "orange",
      completed: "green",
      action_required: "red",
      cancelled: "gray",
    };
    return statusColors[status] || "gray";
  };

  const getStatusIcon = (status) => {
    const statusIcons = {
      initiated: FiUser,
      in_progress: FiClock,
      pending_review: FiAlertCircle,
      completed: FiCheckCircle,
      action_required: FiXCircle,
      cancelled: FiXCircle,
    };
    const IconComponent = statusIcons[status] || FiUser;
    return <IconComponent />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isOverdue = (packet) => {
    return (
      new Date(packet.dueDate) < new Date() && packet.status !== "completed"
    );
  };

  const renderPacketsTable = () => (
    <>
      {/* Filters */}
      <Card mb={6}>
        <CardBody>
          <HStack spacing={4} wrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Select
              placeholder="Filter by status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              maxW="200px"
            >
              <option value="initiated">Initiated</option>
              <option value="in_progress">In Progress</option>
              <option value="pending_review">Pending Review</option>
              <option value="completed">Completed</option>
              <option value="action_required">Action Required</option>
              <option value="cancelled">Cancelled</option>
            </Select>

            <Select
              placeholder="Filter overdue"
              value={filters.overdue}
              onChange={(e) => handleFilterChange("overdue", e.target.value)}
              maxW="150px"
            >
              <option value="true">Overdue Only</option>
              <option value="false">Not Overdue</option>
            </Select>

            <Button
              leftIcon={<FiFilter />}
              onClick={() => {
                setFilters({ status: "", search: "", overdue: "" });
                setSearchTerm("");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              variant="outline"
            >
              Clear Filters
            </Button>
          </HStack>
        </CardBody>
      </Card>

      {/* Packets Table */}
      <Card>
        <CardHeader>
          <Heading size="md">Pre-boarding Packets</Heading>
        </CardHeader>
        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Candidate</Th>
                  <Th>Status</Th>
                  <Th>Progress</Th>
                  <Th>Due Date</Th>
                  <Th>Created</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {packets?.map((packet) => (
                  <Tr key={packet._id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{packet.candidateName}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {packet.candidateEmail}
                        </Text>
                        {packet.metadata?.designation && (
                          <Text fontSize="xs" color="blue.500">
                            {packet.metadata.designation}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <HStack>
                        <Badge
                          colorScheme={getStatusColor(packet.status)}
                          variant="solid"
                        >
                          <HStack spacing={1}>
                            {getStatusIcon(packet.status)}
                            <Text>
                              {packet.status.replace("_", " ").toUpperCase()}
                            </Text>
                          </HStack>
                        </Badge>
                        {isOverdue(packet) && (
                          <Badge colorScheme="red" variant="outline">
                            OVERDUE
                          </Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Progress
                          value={packet.overallProgress || 0}
                          colorScheme="blue"
                          size="sm"
                          w="100px"
                        />
                        <Text fontSize="xs">
                          {Math.round(packet.overallProgress || 0)}% complete
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Text
                        color={isOverdue(packet) ? "red.500" : "inherit"}
                        fontWeight={isOverdue(packet) ? "bold" : "normal"}
                      >
                        {formatDate(packet.dueDate)}
                      </Text>
                    </Td>
                    <Td>{formatDate(packet.createdAt)}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => {
                            setSelectedPacket(packet);
                            onDetailsOpen();
                          }}
                          aria-label="View Details"
                          title="View Details"
                        />
                        <IconButton
                          icon={<FiFileText />}
                          size="sm"
                          variant="outline"
                          colorScheme="purple"
                          onClick={() =>
                            handleViewDocumentDetails(packet.candidateId)
                          }
                          aria-label="View Documents"
                          title="View Documents"
                        />
                        <IconButton
                          icon={<FiMail />}
                          size="sm"
                          variant="outline"
                          colorScheme="orange"
                          onClick={() => {
                            setReminderPacket(packet);
                            onReminderOpen();
                          }}
                          aria-label="Send Reminder"
                          title="Send Reminder"
                          isDisabled={packet.status === "completed"}
                        />
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={() =>
                            navigate(`/recruiting/preboarding/${packet._id}`)
                          }
                        >
                          Review
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {packets.length === 0 && !localLoading && (
            <Box textAlign="center" py={8}>
              <Text color="gray.500">No pre-boarding packets found</Text>
            </Box>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <HStack justify="center" mt={6}>
              <Button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                }
                disabled={!pagination.hasPrev}
                size="sm"
              >
                Previous
              </Button>
              <Text>
                Page {pagination.currentPage} of {pagination.totalPages}
              </Text>
              <Button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                disabled={!pagination.hasNext}
                size="sm"
              >
                Next
              </Button>
            </HStack>
          )}
        </CardBody>
      </Card>
    </>
  );

  return (
    <Box p={6}>
      {/* Header */}
      <VStack align="stretch" spacing={6}>
        <Flex align="center" justify="space-between">
          <Box>
            <Heading size="lg" mb={2}>
              Pre-boarding Management
            </Heading>
            <Text color="gray.600">
              Manage candidate document verification and pre-boarding workflow
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button
              colorScheme="blue"
              leftIcon={<FiFileText />}
              onClick={onUploadOpen}
              size="lg"
            >
              Upload Documents
            </Button>
            <Button
              colorScheme="green"
              leftIcon={<FiUser />}
              onClick={onCreatePacketOpen}
              size="lg"
            >
              Create Packet
            </Button>
          </HStack>
        </Flex>

        {/* Tabs for different views */}
        <Tabs
          colorScheme="blue"
          onChange={(index) => {
            if (index === 2) fetchAcceptedOffers();
          }}
        >
          <TabList>
            <Tab>
              <HStack>
                <FiUser />
                <Text>Packets Overview</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <FiFileText />
                <Text>Document Management</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <FiCheckCircle />
                <Text>Accepted Offers</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Packets Overview Tab */}
            <TabPanel p={0} mt={6}>
              {/* Statistics Cards */}
              <SimpleGrid columns={{ base: 2, md: 6 }} spacing={4} mb={6}>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Total Packets</StatLabel>
                      <StatNumber>{stats.total || 0}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>In Progress</StatLabel>
                      <StatNumber color="yellow.500">
                        {stats.inProgress || 0}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Pending Review</StatLabel>
                      <StatNumber color="orange.500">
                        {stats.pendingReview || 0}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Completed</StatLabel>
                      <StatNumber color="green.500">
                        {stats.completed || 0}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Action Required</StatLabel>
                      <StatNumber color="red.500">
                        {stats.actionRequired || 0}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Overdue</StatLabel>
                      <StatNumber color="red.600">
                        {stats.overdue || 0}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {renderPacketsTable()}
            </TabPanel>

            {/* Document Management Tab */}
            <TabPanel p={0} mt={6}>
              {/* Quick Actions */}
              <Card mb={6} bg="blue.50" borderColor="blue.200" borderWidth={1}>
                <CardBody>
                  <HStack justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold" fontSize="lg">
                        Quick Actions
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Upload documents or manage verification workflow
                      </Text>
                    </VStack>
                    <HStack spacing={3}>
                      <Button
                        colorScheme="blue"
                        leftIcon={<FiFileText />}
                        onClick={onUploadOpen}
                      >
                        Upload Documents
                      </Button>
                      <Button
                        variant="outline"
                        colorScheme="blue"
                        leftIcon={<FiEye />}
                        onClick={() =>
                          navigate("/recruiting/preboarding/verification")
                        }
                      >
                        Review Queue
                      </Button>
                    </HStack>
                  </HStack>
                </CardBody>
              </Card>

              {/* Document Statistics */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Pending Verification</StatLabel>
                      <StatNumber color="orange.500">
                        {documentStats.pendingVerification || 0}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Under Review</StatLabel>
                      <StatNumber color="blue.500">
                        {documentStats.underReview || 0}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Approved</StatLabel>
                      <StatNumber color="green.500">
                        {documentStats.approved || 0}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Expiring Soon</StatLabel>
                      <StatNumber color="red.500">
                        {expiringDocuments.length || 0}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Expiring Documents Alert */}
              {expiringDocuments.length > 0 && (
                <Card mb={6} borderColor="orange.500" borderWidth={2}>
                  <CardHeader>
                    <HStack>
                      <FiAlertCircle color="orange" />
                      <Heading size="sm">Documents Expiring Soon</Heading>
                    </HStack>
                  </CardHeader>
                  <CardBody>
                    <VStack align="stretch" spacing={2}>
                      {expiringDocuments.slice(0, 5).map((doc) => (
                        <HStack key={doc._id} justify="space-between">
                          <Text fontSize="sm">
                            {doc.documentType} - {doc.candidateName}
                          </Text>
                          <Badge colorScheme="orange">
                            Expires: {formatDate(doc.expiryDate)}
                          </Badge>
                        </HStack>
                      ))}
                      {expiringDocuments.length > 5 && (
                        <Text fontSize="xs" color="gray.500">
                          +{expiringDocuments.length - 5} more documents
                        </Text>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <Heading size="md">Document Verification Queue</Heading>
                </CardHeader>
                <CardBody>
                  <Text color="gray.500">
                    Document verification features will be displayed here.
                  </Text>
                  <Button
                    mt={4}
                    colorScheme="blue"
                    onClick={() =>
                      navigate("/recruiting/preboarding/documents")
                    }
                  >
                    Go to Document Management
                  </Button>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Third Tab - Accepted Offers */}
            <TabPanel p={0} mt={6}>
              {acceptedOffersLoading ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="xl" color="blue.500" />
                  <Text mt={4}>Loading accepted offers...</Text>
                </Box>
              ) : (
                <Card>
                  <CardHeader>
                    <HStack justify="space-between">
                      <Heading size="md">Accepted Offers</Heading>
                      <Badge colorScheme="green">
                        {acceptedOffers.length} offer(s)
                      </Badge>
                    </HStack>
                  </CardHeader>
                  <CardBody>
                    {acceptedOffers.length > 0 ? (
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Candidate Name</Th>
                            <Th>Email</Th>
                            <Th>Position</Th>
                            <Th>Salary</Th>
                            <Th>Offer Date</Th>
                            <Th>Status</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {acceptedOffers.map((offer) => (
                            <Tr key={offer._id || offer.id}>
                              <Td>
                                {offer.candidateName ||
                                  offer.candidate?.name ||
                                  "N/A"}
                              </Td>
                              <Td>
                                {offer.candidateEmail ||
                                  offer.candidate?.email ||
                                  "N/A"}
                              </Td>
                              <Td>
                                {offer.position ||
                                  offer.requisition?.title ||
                                  "N/A"}
                              </Td>
                              <Td>
                                {offer.salary
                                  ? `$${offer.salary.toLocaleString()}`
                                  : "N/A"}
                              </Td>
                              <Td>
                                {offer.offerDate
                                  ? formatDate(offer.offerDate)
                                  : offer.createdAt
                                  ? formatDate(offer.createdAt)
                                  : "N/A"}
                              </Td>
                              <Td>
                                <Badge colorScheme="green">
                                  {offer.status || "Accepted"}
                                </Badge>
                              </Td>
                              <Td>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={async () => {
                                    try {
                                      const response =
                                        await preBoardingService.createPacket(
                                          offer._id || offer.id
                                        );
                                      if (response.success) {
                                        toast({
                                          title: "Packet created",
                                          description: `Pre-boarding packet created for ${
                                            offer.candidateName ||
                                            offer.candidate?.name
                                          }`,
                                          status: "success",
                                          duration: 3000,
                                        });
                                        fetchPackets();
                                        navigate(
                                          `/recruiting/preboarding/packets/${response.data._id}`
                                        );
                                      }
                                    } catch (error) {
                                      toast({
                                        title: "Error creating packet",
                                        description:
                                          error.response?.data?.message ||
                                          error.message,
                                        status: "error",
                                        duration: 5000,
                                      });
                                    }
                                  }}
                                >
                                  Create Packet
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    ) : (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500" fontSize="lg">
                          No accepted offers found.
                        </Text>
                        <Text color="gray.400" fontSize="sm" mt={2}>
                          When candidates accept offers, they will appear here.
                        </Text>
                        <Button
                          mt={4}
                          colorScheme="blue"
                          onClick={() => navigate("/recruiting/offers")}
                        >
                          View All Offers
                        </Button>
                      </Box>
                    )}
                  </CardBody>
                </Card>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Send Reminder Modal */}
      <AlertDialog
        isOpen={isReminderOpen}
        onClose={onReminderClose}
        leastDestructiveRef={undefined}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Send Reminder
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to send a reminder email to{" "}
              <strong>{reminderPacket?.candidateName}</strong>?
              {reminderPacket?.remindersSent > 0 && (
                <Text mt={2} fontSize="sm" color="gray.600">
                  Previous reminders sent: {reminderPacket.remindersSent}
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={onReminderClose}>Cancel</Button>
              <Button
                colorScheme="orange"
                onClick={() => handleSendReminder(reminderPacket)}
                ml={3}
              >
                Send Reminder
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Quick Details Modal */}
      <Modal isOpen={isDetailsOpen} onClose={onDetailsClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Pre-boarding Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPacket && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold">Candidate:</Text>
                  <Text>{selectedPacket.candidateName}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {selectedPacket.candidateEmail}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold">Position:</Text>
                  <Text>{selectedPacket.metadata?.designation || "N/A"}</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold">Progress:</Text>
                  <Progress
                    value={selectedPacket.overallProgress || 0}
                    colorScheme="blue"
                    size="lg"
                  />
                  <Text fontSize="sm" mt={1}>
                    {Math.round(selectedPacket.overallProgress || 0)}% complete
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold">Status:</Text>
                  <Badge colorScheme={getStatusColor(selectedPacket.status)}>
                    {selectedPacket.status.replace("_", " ").toUpperCase()}
                  </Badge>
                </Box>

                <Box>
                  <Text fontWeight="bold">Due Date:</Text>
                  <Text
                    color={isOverdue(selectedPacket) ? "red.500" : "inherit"}
                  >
                    {formatDate(selectedPacket.dueDate)}
                    {isOverdue(selectedPacket) && " (Overdue)"}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold">Documents:</Text>
                  <Text>
                    {selectedPacket.documents?.filter(
                      (d) => d.status === "verified"
                    ).length || 0}{" "}
                    of{" "}
                    {selectedPacket.documents?.filter(
                      (d) => d.documentType?.isMandatory
                    ).length || 0}{" "}
                    verified
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDetailsClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                onDetailsClose();
                navigate(`/recruiting/preboarding/${selectedPacket._id}`);
              }}
            >
              Review Documents
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Document Statistics Modal */}
      <Modal
        isOpen={isDocumentStatsOpen}
        onClose={onDocumentStatsClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Document Submission Summary</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {preBoardingContext.submissionSummary && (
              <VStack align="stretch" spacing={4}>
                <SimpleGrid columns={2} spacing={4}>
                  <Card>
                    <CardBody>
                      <Stat>
                        <StatLabel>Total Documents</StatLabel>
                        <StatNumber>
                          {preBoardingContext.submissionSummary.total || 0}
                        </StatNumber>
                      </Stat>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody>
                      <Stat>
                        <StatLabel>Submitted</StatLabel>
                        <StatNumber color="blue.500">
                          {preBoardingContext.submissionSummary.submitted || 0}
                        </StatNumber>
                      </Stat>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody>
                      <Stat>
                        <StatLabel>Approved</StatLabel>
                        <StatNumber color="green.500">
                          {preBoardingContext.submissionSummary.approved || 0}
                        </StatNumber>
                      </Stat>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody>
                      <Stat>
                        <StatLabel>Pending</StatLabel>
                        <StatNumber color="orange.500">
                          {preBoardingContext.submissionSummary.pending || 0}
                        </StatNumber>
                      </Stat>
                    </CardBody>
                  </Card>
                </SimpleGrid>

                <Box>
                  <Text fontWeight="bold" mb={2}>
                    By Category:
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {preBoardingContext.submissionSummary.byCategory &&
                      Object.entries(
                        preBoardingContext.submissionSummary.byCategory
                      ).map(([category, stats]) => (
                        <HStack key={category} justify="space-between">
                          <Text textTransform="capitalize">{category}:</Text>
                          <Badge colorScheme="blue">
                            {stats.approved || 0} / {stats.total || 0}
                          </Badge>
                        </HStack>
                      ))}
                  </VStack>
                </Box>

                {preBoardingContext.documents.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Recent Documents:
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {preBoardingContext.documents.slice(0, 5).map((doc) => (
                        <HStack key={doc._id} justify="space-between">
                          <Text fontSize="sm">{doc.documentType}</Text>
                          <Badge
                            colorScheme={
                              doc.status === "approved"
                                ? "green"
                                : doc.status === "rejected"
                                ? "red"
                                : "yellow"
                            }
                          >
                            {doc.status}
                          </Badge>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}

            {preBoardingContext.loading && (
              <Box textAlign="center" py={8}>
                <Text>Loading document details...</Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDocumentStatsClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                onDocumentStatsClose();
                navigate(
                  `/recruiting/preboarding/documents/${selectedCandidate}`
                );
              }}
            >
              View All Documents
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Document Upload Modal */}
      <Modal isOpen={isUploadOpen} onClose={onUploadClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="90vw">
          <ModalHeader>Upload Pre-boarding Documents</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedCandidate ? (
              <DocumentUpload
                candidateId={selectedCandidate}
                onUploadComplete={() => {
                  onUploadClose();
                  fetchPackets();
                  fetchVerificationStats();
                }}
              />
            ) : (
              <Box>
                <Text mb={4} color="gray.600">
                  Please select a candidate to upload documents:
                </Text>
                <Select
                  placeholder="Select candidate"
                  onChange={(e) => setSelectedCandidate(e.target.value)}
                  size="lg"
                >
                  {packets.map((packet) => (
                    <option key={packet._id} value={packet.candidateId}>
                      {packet.candidateName} - {packet.candidateEmail}
                    </option>
                  ))}
                </Select>
                {packets.length === 0 && (
                  <Text fontSize="sm" color="red.500" mt={2}>
                    No candidates available. Please create a pre-boarding packet
                    first.
                  </Text>
                )}
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Create Packet Modal */}
      <Modal
        isOpen={isCreatePacketOpen}
        onClose={onCreatePacketClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Pre-boarding Packet</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color="gray.600">
                Create a pre-boarding packet from an existing accepted offer.
              </Text>

              <Box>
                <Text fontWeight="bold" mb={2}>
                  Select an Offer
                </Text>
                <Select
                  placeholder={
                    offersLoading ? "Loading offers..." : "Select an offer"
                  }
                  value={selectedOfferId}
                  onChange={(e) => setSelectedOfferId(e.target.value)}
                  isDisabled={offersLoading}
                >
                  {offers.map((offer) => (
                    <option
                      key={offer._id || offer.id}
                      value={offer._id || offer.id}
                    >
                      {offer.candidateName ||
                        offer.candidate?.name ||
                        offer.candidateEmail ||
                        `Offer ${offer._id || offer.id}`}
                    </option>
                  ))}
                </Select>

                {offers.length === 0 && !offersLoading && (
                  <Box mt={3} bg="blue.50" p={3} borderRadius="md">
                    <Text fontSize="sm">
                      No offers found. You can create an offer in the Offers
                      module.
                    </Text>
                    <Button
                      mt={2}
                      size="sm"
                      onClick={() => navigate("/recruiting/offers")}
                    >
                      Go to Offers
                    </Button>
                  </Box>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreatePacketClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              isLoading={offersLoading}
              onClick={handleCreatePacketFromOffer}
            >
              Create Packet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PreBoardingDashboard;
